import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  Mail, RefreshCw, Copy, Shuffle, Trash2, Inbox, X, Clock, ShieldCheck, Moon, Sun, History,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const timeAgo = (iso) => {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "baru saja";
  if (diff < 3600) return `${Math.floor(diff / 60)} mnt lalu`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} jam lalu`;
  return new Date(iso).toLocaleDateString("id-ID", { day: "numeric", month: "short" });
};

const linkifyBody = (text) =>
  text.split(/((?:https?:\/\/|www\.)[^\s<>"'\])]+)/g).map((part, i) => {
    if (!/^(https?:\/\/|www\.)/.test(part)) return part;
    const m = part.match(/[.,;:!?]+$/);
    const url = m ? part.slice(0, -m[0].length) : part;
    const trail = m ? m[0] : "";
    const href = url.startsWith("www.") ? `https://${url}` : url;
    return (
      <span key={i}>
        <a href={href} target="_blank" rel="noopener noreferrer" className="body-link" data-testid="email-body-link">{url}</a>
        {trail}
      </span>
    );
  });

function HtmlEmailFrame({ html }) {
  const ref = useRef(null);
  const [h, setH] = useState(300);
  const doc = `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank"><style>body{font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#334155;margin:0;padding:6px;word-break:break-word}img{max-width:100%;height:auto}table{max-width:100%}</style></head><body>${html}</body></html>`;
  return (
    <iframe
      ref={ref}
      title="email-html"
      sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
      srcDoc={doc}
      className="html-frame"
      style={{ height: h }}
      onLoad={() => {
        try {
          setH(Math.min(Math.max(ref.current.contentDocument.body.scrollHeight + 30, 200), 5000));
        } catch (e) { /* sandbox */ }
      }}
      data-testid="email-html-frame"
    />
  );
}

export default function App() {
  const [domains, setDomains] = useState([]);
  const [realDomains, setRealDomains] = useState([]);
  const [local, setLocal] = useState("");
  const [domain, setDomain] = useState("");
  const [activeAddr, setActiveAddr] = useState(null); // {local, domain, full}
  const [emails, setEmails] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewHtml, setViewHtml] = useState(true);
  const [dark, setDark] = useState(() => localStorage.getItem("mk-theme") === "dark");
  const [history, setHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem("mk-history")) || []; } catch { return []; }
  });
  const pollRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("mk-theme", dark ? "dark" : "light");
  }, [dark]);

  const saveHistory = (addr) => {
    setHistory((prev) => {
      const next = [addr, ...prev.filter((h) => h.full !== addr.full)].slice(0, 8);
      localStorage.setItem("mk-history", JSON.stringify(next));
      return next;
    });
  };

  const removeHistory = (full) => {
    setHistory((prev) => {
      const next = prev.filter((h) => h.full !== full);
      localStorage.setItem("mk-history", JSON.stringify(next));
      return next;
    });
  };

  const openFromHistory = async (h) => {
    setLocal(h.local);
    setDomain(h.domain);
    setActiveAddr(h);
    setSelected(null);
    await fetchInbox(h);
  };

  useEffect(() => {
    axios.get(`${API}/domains`).then((r) => {
      setDomains(r.data.domains);
      setRealDomains(r.data.real_domains || []);
      setDomain(r.data.domains[0]);
    });
  }, []);

  const fetchInbox = useCallback(async (addr, silent = false) => {
    if (!addr) return;
    if (!silent) setLoading(true);
    try {
      const r = await axios.get(`${API}/inbox/${addr.local}/${addr.domain}`);
      setEmails(r.data.emails);
      const [rl, rd] = r.data.address.split("@");
      const fixed = { local: rl, domain: rd, full: r.data.address };
      saveHistory(fixed);
      if (r.data.address !== addr.full) {
        setActiveAddr(fixed);
        setLocal(rl);
      }
    } catch (e) {
      if (!silent) toast.error(e.response?.data?.detail || "Gagal memuat inbox");
    }
    if (!silent) setLoading(false);
  }, []);

  useEffect(() => {
    if (!activeAddr) return;
    pollRef.current = setInterval(() => fetchInbox(activeAddr, true), 10000);
    return () => clearInterval(pollRef.current);
  }, [activeAddr, fetchInbox]);

  const checkInbox = async () => {
    const clean = local.trim().toLowerCase();
    if (!clean) { toast.error("Masukkan nama alamat dulu"); return; }
    const addr = { local: clean, domain, full: `${clean}@${domain}` };
    setActiveAddr(addr);
    setSelected(null);
    await fetchInbox(addr);
  };

  const randomAddress = async () => {
    const r = await axios.get(`${API}/random-address`);
    setLocal(r.data.local);
    setDomain(r.data.domain);
    const addr = { local: r.data.local, domain: r.data.domain, full: `${r.data.local}@${r.data.domain}` };
    setActiveAddr(addr);
    setSelected(null);
    await fetchInbox(addr);
  };

  const copyAddress = () => {
    if (!activeAddr) { toast.error("Cek inbox dulu"); return; }
    navigator.clipboard.writeText(activeAddr.full);
    toast.success("Alamat disalin!");
  };

  const openEmail = async (id) => {
    try {
      const r = await axios.get(`${API}/email/${id}`, { params: { local: activeAddr?.local, domain: activeAddr?.domain } });
      setSelected(r.data);
      setViewHtml(true);
      setEmails((prev) => prev.map((e) => (e.id === id ? { ...e, read: true } : e)));
    } catch (e) {
      toast.error("Gagal membuka email");
    }
  };

  const deleteEmail = async (id) => {
    try {
      await axios.delete(`${API}/email/${id}`, { params: { local: activeAddr?.local, domain: activeAddr?.domain } });
      setEmails((prev) => prev.filter((e) => e.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Email dihapus");
    } catch (e) {
      toast.error("Gagal menghapus email");
    }
  };

  const clearInbox = async () => {
    if (!activeAddr) return;
    try {
      await axios.delete(`${API}/inbox/${activeAddr.local}/${activeAddr.domain}`);
      setEmails([]);
      setSelected(null);
      toast.success("Inbox dikosongkan");
    } catch (e) {
      toast.error("Gagal mengosongkan inbox");
    }
  };

  const unread = emails.filter((e) => !e.read).length;
  const isRealActive = activeAddr && realDomains.includes(activeAddr.domain);

  return (
    <div className={`ym-app ${dark ? "ym-dark" : ""}`}>
      <Toaster position="top-center" richColors />
      {/* Header */}
      <header className="ym-header" data-testid="app-header">
        <div className="ym-header-inner">
          <div className="ym-logo">
            <div className="ym-logo-mark"><Mail size={18} /></div>
            <span>Mail<em>Kilat</em></span>
            <span className="ym-badge">Email Sementara</span>
          </div>
          <div className="ym-header-right">
            {activeAddr && (
              <div className="ym-live" data-testid="active-address-display">
                <span className="pulse-dot" />
                <span className="mono">{activeAddr.full}</span>
                {isRealActive && <span className="real-badge">ASLI</span>}
                <span className="ym-live-note"><Clock size={12} /> auto-refresh 10 dtk</span>
              </div>
            )}
            <button
              className="theme-toggle"
              onClick={() => setDark((d) => !d)}
              title={dark ? "Mode terang" : "Mode gelap"}
              data-testid="button-theme-toggle"
            >
              {dark ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          </div>
        </div>
      </header>

      {/* Address bar */}
      <div className="ym-addressbar">
        <div className="ym-addressbar-inner">
          <div className="ym-input-group">
            <input
              data-testid="input-local-part"
              placeholder="nama.anda"
              value={local}
              onChange={(e) => setLocal(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && checkInbox()}
            />
            <span className="at">@</span>
            <select data-testid="select-domain" value={domain} onChange={(e) => setDomain(e.target.value)}>
              {domains.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <button className="btn-primary" onClick={checkInbox} data-testid="button-check-inbox">
            <Inbox size={15} /> Cek Inbox
          </button>
          <button className="btn-ghost" onClick={randomAddress} data-testid="button-random-address">
            <Shuffle size={15} /> Acak
          </button>
          <button className="btn-ghost" onClick={copyAddress} data-testid="button-copy-address">
            <Copy size={15} /> Salin
          </button>
        </div>
        {history.length > 0 && (
          <div className="ym-history" data-testid="container-history">
            <span className="ym-history-label"><History size={13} /> Riwayat:</span>
            {history.map((h) => (
              <span key={h.full} className={`history-chip ${activeAddr?.full === h.full ? "active" : ""}`}>
                <button className="history-chip-open mono" onClick={() => openFromHistory(h)} data-testid="button-history-address">
                  {h.full}
                </button>
                <button className="history-chip-remove" onClick={() => removeHistory(h.full)} title="Hapus dari riwayat" data-testid="button-history-remove">
                  <X size={11} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Main */}
      <main className="ym-main">
        {!activeAddr ? (
          <div className="ym-hero" data-testid="landing-hero">
            <div className="ym-hero-icon"><ShieldCheck size={40} /></div>
            <h1>Inbox Email Sementara, Tanpa Daftar</h1>
            <p>Ketik nama alamat apa pun, pilih domain, dan langsung cek inboxnya. Semua alamat <strong>menerima email sungguhan</strong> — cocok untuk kode verifikasi saat daftar di website.</p>
            <div className="ym-hero-actions">
              <button className="btn-primary lg" onClick={randomAddress} data-testid="button-hero-random">
                <Shuffle size={16} /> Buat Alamat Acak
              </button>
            </div>
            <div className="ym-domains-list">
              {domains.map((d) => (
                <span key={d} className={`domain-chip mono ${realDomains.includes(d) ? "real" : ""}`}>
                  @{d}{realDomains.includes(d) && " ✓"}
                </span>
              ))}
            </div>
          </div>
        ) : (
          <div className="ym-panes">
            {/* Inbox list */}
            <section className="ym-inbox-pane">
              <div className="ym-inbox-head">
                <h2><Inbox size={15} /> Inbox {unread > 0 && <span className="unread-count" data-testid="unread-count">{unread}</span>}</h2>
                <div className="ym-inbox-actions">
                  <button className={`icon-btn ${loading ? "spin" : ""}`} title="Refresh" onClick={() => fetchInbox(activeAddr)} data-testid="button-refresh-inbox"><RefreshCw size={14} /></button>
                  <button className="icon-btn danger" title="Kosongkan inbox" onClick={clearInbox} data-testid="button-clear-inbox"><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="ym-inbox-list" data-testid="container-inbox-list">
                {emails.length === 0 ? (
                  <div className="ym-empty-list" data-testid="empty-inbox">
                    <Mail size={26} />
                    <p>Inbox kosong. Gunakan alamat ini untuk mendaftar di website mana pun — kode verifikasi akan muncul di sini.</p>
                  </div>
                ) : (
                  emails.map((e) => (
                    <button
                      key={e.id}
                      className={`ym-item ${selected?.id === e.id ? "active" : ""} ${!e.read ? "unread" : ""}`}
                      onClick={() => openEmail(e.id)}
                      data-testid="item-inbox-email"
                    >
                      <div className="ym-item-top">
                        <span className="sender">{e.sender_name || e.sender}{e.source === "real" && <span className="real-badge">ASLI</span>}</span>
                        <span className="time">{timeAgo(e.created_at)}</span>
                      </div>
                      <div className="subject">{!e.read && <span className="dot" />}{e.subject}</div>
                      <div className="snippet">{e.snippet}</div>
                    </button>
                  ))
                )}
              </div>
            </section>

            {/* Reading pane */}
            <section className="ym-read-pane" data-testid="container-reading-pane">
              {selected ? (
                <div className="ym-email">
                  <div className="ym-email-head">
                    <h2 data-testid="text-email-subject">{selected.subject}</h2>
                    <div className="ym-email-head-actions">
                      {selected.body_html && (
                        <div className="view-toggle" data-testid="view-toggle">
                          <button className={viewHtml ? "active" : ""} onClick={() => setViewHtml(true)} data-testid="button-view-html">HTML</button>
                          <button className={!viewHtml ? "active" : ""} onClick={() => setViewHtml(false)} data-testid="button-view-text">Teks</button>
                        </div>
                      )}
                      <button className="icon-btn danger" onClick={() => deleteEmail(selected.id)} data-testid="button-delete-email"><Trash2 size={15} /></button>
                    </div>
                  </div>
                  <div className="ym-email-meta">
                    <div className="avatar">{(selected.sender_name || selected.sender)[0].toUpperCase()}</div>
                    <div>
                      <div className="from" data-testid="text-email-sender">{selected.sender_name || selected.sender}{selected.source === "real" && <span className="real-badge">ASLI</span>}</div>
                      <div className="mono small">{selected.sender} • kepada {selected.address}</div>
                    </div>
                    <div className="date">{new Date(selected.created_at).toLocaleString("id-ID")}</div>
                  </div>
                  {selected.body_html && viewHtml ? (
                    <HtmlEmailFrame html={selected.body_html} />
                  ) : (
                    <pre className="ym-email-body" data-testid="text-email-body">{linkifyBody(selected.body)}</pre>
                  )}
                </div>
              ) : (
                <div className="ym-empty-read" data-testid="empty-reading-pane">
                  <Mail size={40} />
                  <p>Pilih email di daftar untuk membacanya</p>
                </div>
              )}
            </section>
          </div>
        )}
      </main>

      <footer className="ym-footer">
        MailKilat — email sementara asli (jaringan Mail.tm). Semua alamat menerima email sungguhan dari internet.
      </footer>
    </div>
  );
}
